import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal, ModalContent, ModalHeader, ModalTitle, ModalDescription, ModalBody, ModalFooter, useToast } from '../sectepe-ui/index';
import { useAuth } from '../contexts/AuthContext';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';
import { APP_ROLLEN, istAppRolle, type AppRolle } from '../berechtigungen';
import tenantService, { InviteUserData, Tenant, UpdateUserRoleData } from '../services/tenant.service';
import { RechteDialog, hatAngepassteRechte, type RechteAenderung } from '../verwaltung/RechteDialog';
import { User } from '../services/auth.service';
import { PageHeader, EmptyState, LoadingState, StatusBadge, useConfirm } from '../components/ui';
import { Icon } from '../components/icons';
import { KpiRow } from '../vertrieb/KpiRow';
import { fehlerText } from '../vertrieb/format';
import { APP_ROLLEN_LABEL, angezeigteAppRolle, appRolleLabel, mandantenRolle, systemRolle, initialen } from '../verwaltung/rollen';

/** Tooltip des Badges "Rechte angepasst": was erteilt und was entzogen ist. */
const rechteTitel = (u: User): string => [
  u.rechteZusatz?.length ? `Zusätzlich: ${u.rechteZusatz.join(', ')}` : '',
  u.rechteEntzug?.length ? `Entzogen: ${u.rechteEntzug.join(', ')}` : '',
].filter(Boolean).join(' · ');

const LEERE_EINLADUNG: InviteUserData = { email: '', name: '', password: '', role: 'user', tenantRole: 'member', appRolle: 'mitarbeit' };

/**
 * Mandantenrollen, die im Entwicklungsmodus vergeben werden. Die alte Rolle
 * 'admin' wird nicht mehr angeboten; sie hatte im Backend nie eine Wirkung
 * (Verbesserungsplan, Paket 2 Schritt 6).
 */
const MANDANTEN_ROLLEN_AUSWAHL = ['member', 'owner'] as const;
type MandantenRolleAuswahl = (typeof MANDANTEN_ROLLEN_AUSWAHL)[number];

/** Mandantenrolle fuer die Formulare: alles ausser owner zaehlt als member (auch alte 'admin'-Eintraege). */
const mandantenRolleAuswahl = (r?: string | null): MandantenRolleAuswahl => (r === 'owner' ? 'owner' : 'member');

/** Die gespeicherte Spalte appRolle einer Person (nicht die wirksame Rolle, die owner/admin zu Inhaber:in macht). */
const gespeicherteAppRolle = (u: { appRolle?: string | null }): AppRolle => (istAppRolle(u.appRolle) ? u.appRolle : 'mitarbeit');

/**
 * Eigentuemer:innen sind im Backend immer Inhaber:in (appRolleVon); das
 * Formular haelt beide Felder dazu passend. Wer vom Eigentum herabgestuft
 * wird, faellt auf die gespeicherte Rolle (`rueckfall`) zurueck -- sonst
 * bliebe die nur angezeigte "Inhaber:in" als Spalte stehen und die Person
 * behielte alle Rechte.
 */
function rollenAbgleich<T extends { tenantRole?: string; appRolle?: string }>(
  form: T, feld: 'tenantRole' | 'appRolle', wert: string, rueckfall: AppRolle = 'mitarbeit',
): T {
  if (feld === 'tenantRole') {
    if (wert === 'owner') return { ...form, tenantRole: wert, appRolle: 'inhaber' };
    return { ...form, tenantRole: wert, ...(form.appRolle === 'inhaber' ? { appRolle: rueckfall } : {}) };
  }
  return { ...form, appRolle: wert };
}

interface RollenFelderProps {
  tenantRole?: string;
  appRolle?: string;
  role?: string;
  /** Systemrolle (Plattform-Admin) uebernimmt das Backend nur von Plattform-Admins; sonst bleibt das Feld weg. */
  zeigeSystemrolle: boolean;
  onChange: (feld: 'tenantRole' | 'appRolle' | 'role', wert: string) => void;
}

/** Die Rollenfelder von Einladung und Rollenwechsel (nur Entwicklungsmodus ohne Keycloak). */
const RollenFelder: React.FC<RollenFelderProps> = ({ tenantRole, appRolle, role, zeigeSystemrolle, onChange }) => {
  const istEigentuemer = tenantRole === 'owner';
  const rolle: AppRolle = istAppRolle(appRolle) ? appRolle : 'mitarbeit';
  return (
    <>
      <label>
        Rolle
        <select value={rolle} disabled={istEigentuemer} onChange={(e) => onChange('appRolle', e.target.value)}>
          {APP_ROLLEN.map((r) => <option key={r} value={r}>{APP_ROLLEN_LABEL[r].label}</option>)}
        </select>
      </label>
      <p className="text-xs text-secondary">{APP_ROLLEN_LABEL[rolle].hinweis}</p>
      <label>
        Mandanten-Rolle
        <select value={mandantenRolleAuswahl(tenantRole)} onChange={(e) => onChange('tenantRole', e.target.value)}>
          {MANDANTEN_ROLLEN_AUSWAHL.map((r) => <option key={r} value={r}>{mandantenRolle(r).label}</option>)}
        </select>
      </label>
      <p className="text-xs text-secondary">
        {istEigentuemer
          ? 'Eigentümer:innen des Mandanten zählen immer als Inhaber:in; die letzte Eigentümer:in kann nicht entfernt werden.'
          : 'Mitglieder arbeiten mit der gewählten Rolle; der Mandant selbst gehört den Eigentümer:innen.'}
      </p>
      {zeigeSystemrolle && (
        <label>
          Systemrolle
          <select value={role ?? 'user'} onChange={(e) => onChange('role', e.target.value)}>
            <option value="user">{systemRolle('user')}</option>
            <option value="admin">{systemRolle('admin')}</option>
          </select>
        </label>
      )}
    </>
  );
};

/**
 * Benutzer des eigenen Mandanten. Die Seite verlangt `admin.benutzer`
 * (Einladen, Entfernen, Rolle im Entwicklungsmodus). Im Keycloak-Betrieb
 * (ssoEnabled) werden Konten und Rollen dort gepflegt: Einladen und
 * "Rolle aendern" fehlen, ein Hinweis erklaert den Weg; das Backend
 * antwortet auf beides ohnehin mit 403 (Verbesserungsplan, Paket 2 Schritt 6).
 * "Rechte anpassen" (Rechte je Person, Entscheidung 17) gibt es in beiden
 * Betriebsarten, ausser fuer Inhaber:innen (sie haben ohnehin alles).
 */
const UserManagementPage: React.FC = () => {
  const { user: ich } = useAuth();
  const { darf, ssoEnabled } = useBerechtigung();
  const { push } = useToast();
  const { confirm } = useConfirm();
  const [benutzer, setBenutzer] = useState<User[]>([]);
  const [mandant, setMandant] = useState<Tenant | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);

  const [zeigeEinladung, setZeigeEinladung] = useState(false);
  const [einladung, setEinladung] = useState<InviteUserData>(LEERE_EINLADUNG);
  const [sendet, setSendet] = useState(false);

  const [rollenFuer, setRollenFuer] = useState<User | null>(null);
  const [rollen, setRollen] = useState<UpdateUserRoleData>({ role: 'user', tenantRole: 'member', appRolle: 'mitarbeit' });
  const [rechteFuer, setRechteFuer] = useState<User | null>(null);

  const darfVerwalten = darf('admin.benutzer');
  // Einladen und Rollen vergeben gibt es nur ohne Keycloak; Entfernen bleibt.
  const darfVergeben = darfVerwalten && !ssoEnabled;
  const darfMandant = darf('admin.mandant');
  const istPlattformAdmin = ich?.role === 'admin';
  const mandantId = ich?.tenantId;

  const laden = useCallback(async () => {
    if (!mandantId) { setLaedt(false); return; }
    try {
      const [liste, m] = await Promise.all([
        tenantService.getTenantUsers(mandantId),
        Promise.resolve().then(() => tenantService.getTenant(mandantId)).catch(() => null),
      ]);
      setBenutzer(liste ?? []);
      setMandant(m ?? null);
      setFehler(null);
    } catch (e) {
      setFehler(fehlerText(e, 'Benutzer konnten nicht geladen werden'));
    } finally {
      setLaedt(false);
    }
  }, [mandantId]);

  useEffect(() => { laden(); }, [laden]);

  const einladen = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mandantId) return;
    setSendet(true);
    try {
      await tenantService.inviteUser(mandantId, { ...einladung, email: einladung.email.trim().toLowerCase(), name: einladung.name.trim() });
      push({ message: `${einladung.name.trim()} eingeladen.`, severity: 'success' });
      setZeigeEinladung(false);
      setEinladung(LEERE_EINLADUNG);
      await laden();
    } catch (err) {
      setFehler(fehlerText(err, 'Einladen fehlgeschlagen'));
      setZeigeEinladung(false);
    } finally {
      setSendet(false);
    }
  };

  const entfernen = async (u: User) => {
    if (!mandantId) return;
    const ok = await confirm({
      title: `${u.name} entfernen?`,
      message: `${u.email} verliert den Zugang zu diesem Mandanten. Inhalte, die ${u.name} angelegt hat, bleiben erhalten.${ssoEnabled ? ' Entfernen wirkt nur bis zur nächsten Anfrage der Person mit gültigem Keycloak-Login; dauerhaft gesperrt ist der Zugang erst, wenn das Konto in Keycloak deaktiviert wird.' : ''}`,
      confirmLabel: 'Entfernen',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await tenantService.removeUser(mandantId, u.id);
      push({ message: `${u.name} entfernt.`, severity: 'info' });
      await laden();
    } catch (err) {
      setFehler(fehlerText(err, 'Entfernen fehlgeschlagen'));
    }
  };

  const rollenOeffnen = (u: User) => {
    setRollenFuer(u);
    // Vorbelegung: Eigentuemer:innen zeigen Inhaber:in (das Feld ist gesperrt), alle anderen ihre
    // gespeicherte Spalte appRolle -- auch Plattform-Admins, deren wirksame Rolle das Backend ableitet.
    setRollen({ role: u.role, tenantRole: mandantenRolleAuswahl(u.tenantRole), appRolle: u.tenantRole === 'owner' ? 'inhaber' : gespeicherteAppRolle(u) });
  };

  const rollenSpeichern = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mandantId || !rollenFuer) return;
    try {
      await tenantService.updateUserRole(mandantId, rollenFuer.id, rollen);
      push({ message: `Rolle von ${rollenFuer.name} geändert.`, severity: 'success' });
      setRollenFuer(null);
      await laden();
    } catch (err) {
      setFehler(fehlerText(err, 'Rolle konnte nicht geändert werden'));
      setRollenFuer(null);
    }
  };

  const rechteSpeichern = async (u: User, aenderung: RechteAenderung) => {
    if (!mandantId) return;
    try {
      await tenantService.updateUserRechte(mandantId, u.id, aenderung);
      push({ message: `Rechte von ${u.name} gespeichert.`, severity: 'success' });
      setRechteFuer(null);
      await laden();
    } catch (err) {
      setFehler(fehlerText(err, 'Rechte konnten nicht gespeichert werden'));
      setRechteFuer(null);
    }
  };

  if (!mandantId) {
    return (
      <div className="page-container">
        <PageHeader eyebrow="Verwaltung" title="Benutzer" />
        <EmptyState icon="users" title="Sie sind keinem Mandanten zugeordnet" description="Benutzer werden je Mandant verwaltet. Bitte wenden Sie sich an die Inhaber:in Ihres Mandanten." />
      </div>
    );
  }

  const inhaberinnen = benutzer.filter((u) => angezeigteAppRolle(u) === 'inhaber').length;
  const frei = mandant ? Math.max(0, mandant.maxUsers - benutzer.length) : null;
  const voll = frei === 0;

  return (
    <div className="page-container">
      <PageHeader
        eyebrow="Verwaltung"
        title="Benutzer"
        subtitle={darfVerwalten ? 'Wer im Mandanten arbeitet und was er darf.' : 'Wer im Mandanten arbeitet. Einladen und Entfernen dürfen Inhaber:innen.'}
        actions={darfVergeben ? (
          <button type="button" className="btn btn-primary" onClick={() => setZeigeEinladung(true)} disabled={voll} title={voll ? 'Alle Plätze belegt – unter Mandant erhöhen' : undefined}>
            <Icon name="plus" size={14} /> Benutzer einladen
          </button>
        ) : undefined}
      />

      {ssoEnabled && (
        <div className="alert alert-info" role="note" aria-label="Hinweis zu Keycloak">
          <Icon name="info" size={16} />
          <span>
            Benutzer und Rollen werden in Keycloak gepflegt (Users → Role mapping, Rollen <code>intermal-*</code>); sie erscheinen hier nach dem ersten Login.
            Entfernen wirkt nur bis zur nächsten Anfrage der Person mit gültigem Keycloak-Login. Dauerhaft gesperrt ist der Zugang erst, wenn das Konto in Keycloak deaktiviert wird;
            eine entfernte <code>intermal-*</code>-Rolle allein lässt die Person als Mitarbeit weiterarbeiten.
          </span>
        </div>
      )}

      {fehler && <div className="alert alert-error">{fehler}</div>}

      {laedt ? (
        <LoadingState rows={3} label="Benutzer werden geladen" />
      ) : (
        <>
          <KpiRow
            aria-label="Benutzer-Kennzahlen"
            items={[
              { label: 'Benutzer', value: benutzer.length, hint: mandant ? `von ${mandant.maxUsers} Plätzen` : undefined },
              { label: 'Inhaber:innen', value: inhaberinnen },
              ...(frei !== null ? [{ label: 'freie Plätze', value: frei, tone: voll ? 'warn' as const : 'neutral' as const, to: darfMandant ? '/tenant-management' : undefined, hint: voll ? 'unter Mandant erhöhen' : undefined }] : []),
            ]}
          />

          {benutzer.length === 0 ? (
            <EmptyState
              icon="users"
              title="Noch keine Benutzer"
              description={ssoEnabled ? 'Benutzer erscheinen hier nach ihrem ersten Login über Keycloak.' : undefined}
              action={darfVergeben ? { label: 'Benutzer einladen', onClick: () => setZeigeEinladung(true) } : undefined}
            />
          ) : (
            <div className="table-container">
              <table className="table benutzer-tabelle">
                <thead>
                  <tr>
                    <th>Benutzer</th><th>Rolle</th><th>Status</th>
                    {darfVerwalten && <th className="r">Aktionen</th>}
                  </tr>
                </thead>
                <tbody>
                  {benutzer.map((u) => {
                    const r = appRolleLabel(u);
                    const selbst = u.id === ich?.id;
                    return (
                      <tr key={u.id}>
                        <td>
                          <div className="benutzer-zelle">
                            <span className="benutzer-avatar" aria-hidden="true">{initialen(u.name)}</span>
                            <span>
                              <strong>{u.name}</strong>{selbst && <span className="text-xs text-secondary"> (Sie)</span>}
                              <div className="text-xs text-secondary">{u.email}</div>
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="benutzer-rollen">
                            <StatusBadge tone={r.tone} title={r.hinweis}>{r.label}</StatusBadge>
                            {/* Plattform-Admin (alle Mandanten) ist mehr als die App-Rolle; "Benutzer" waere nur Rauschen. */}
                            {u.role === 'admin' && <StatusBadge tone="neutral" title="Plattform-Administration, sieht alle Mandanten">{systemRolle(u.role)}</StatusBadge>}
                            {hatAngepassteRechte(u) && angezeigteAppRolle(u) !== 'inhaber' && (
                              <StatusBadge tone="warning" title={rechteTitel(u)}>Rechte angepasst</StatusBadge>
                            )}
                          </div>
                        </td>
                        <td>{u.isActive === false ? <StatusBadge tone="danger">Inaktiv</StatusBadge> : <StatusBadge tone="success">Aktiv</StatusBadge>}</td>
                        {darfVerwalten && (
                          <td className="r">
                            {!selbst && (
                              <div className="btn-group benutzer-aktionen">
                                {darfVergeben && (
                                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => rollenOeffnen(u)}>Rolle ändern</button>
                                )}
                                {angezeigteAppRolle(u) !== 'inhaber' && (
                                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => setRechteFuer(u)} aria-label={`Rechte von ${u.name} anpassen`}>Rechte anpassen</button>
                                )}
                                <button type="button" className="btn btn-sm btn-ghost" onClick={() => entfernen(u)} aria-label={`${u.name} entfernen`} title="Entfernen">
                                  <Icon name="trash" size={14} />
                                </button>
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <dl className="sales-facts rollen-legende" aria-label="Rollen">
            {APP_ROLLEN.map((rolle) => (
              <div key={rolle}><dt>{APP_ROLLEN_LABEL[rolle].label}</dt><dd>{APP_ROLLEN_LABEL[rolle].hinweis}</dd></div>
            ))}
          </dl>
          {darfMandant && <p className="text-xs text-secondary">Anzahl der Plätze und Kontaktadresse stehen unter <Link to="/tenant-management">Mandant</Link>.</p>}
        </>
      )}

      {darfVergeben && (
        <Modal open={zeigeEinladung} onOpenChange={(o) => { if (!o) setZeigeEinladung(false); }}>
          <ModalContent>
            <form onSubmit={einladen} aria-label="Benutzer einladen">
              <ModalHeader>
                <ModalTitle>Benutzer einladen</ModalTitle>
                <ModalDescription>Das Startpasswort bitte auf sicherem Weg weitergeben; ändern lässt es sich danach unter Profil.</ModalDescription>
              </ModalHeader>
              <ModalBody>
                <div className="akte-felder">
                  <label>Name *<input required autoFocus value={einladung.name} onChange={(e) => setEinladung({ ...einladung, name: e.target.value })} /></label>
                  <label>E-Mail *<input type="email" required value={einladung.email} onChange={(e) => setEinladung({ ...einladung, email: e.target.value })} /></label>
                  <label>Startpasswort * (mind. 8 Zeichen)<input type="password" required minLength={8} autoComplete="new-password" value={einladung.password} onChange={(e) => setEinladung({ ...einladung, password: e.target.value })} /></label>
                  <RollenFelder
                    tenantRole={einladung.tenantRole}
                    appRolle={einladung.appRolle}
                    role={einladung.role}
                    zeigeSystemrolle={istPlattformAdmin}
                    onChange={(feld, wert) => setEinladung((f) => (feld === 'role' ? { ...f, role: wert } : rollenAbgleich(f, feld, wert)))}
                  />
                </div>
              </ModalBody>
              <ModalFooter>
                <button type="button" className="btn btn-ghost" onClick={() => setZeigeEinladung(false)}>Abbrechen</button>
                <button type="submit" className="btn btn-primary" disabled={sendet}>{sendet ? 'Lädt ein…' : 'Einladen'}</button>
              </ModalFooter>
            </form>
          </ModalContent>
        </Modal>
      )}

      {darfVerwalten && <RechteDialog person={rechteFuer} onSpeichern={rechteSpeichern} onSchliessen={() => setRechteFuer(null)} />}

      {darfVergeben && (
        <Modal open={Boolean(rollenFuer)} onOpenChange={(o) => { if (!o) setRollenFuer(null); }}>
          {rollenFuer && (
            <ModalContent>
              <form onSubmit={rollenSpeichern} aria-label="Rolle ändern">
                <ModalHeader>
                  <ModalTitle>Rolle von {rollenFuer.name}</ModalTitle>
                  <ModalDescription>{rollenFuer.email}</ModalDescription>
                </ModalHeader>
                <ModalBody>
                  <div className="akte-felder">
                    <RollenFelder
                      tenantRole={rollen.tenantRole}
                      appRolle={rollen.appRolle}
                      role={rollen.role}
                      zeigeSystemrolle={istPlattformAdmin}
                      onChange={(feld, wert) => setRollen((f) => (feld === 'role' ? { ...f, role: wert } : rollenAbgleich(f, feld, wert, gespeicherteAppRolle(rollenFuer))))}
                    />
                  </div>
                </ModalBody>
                <ModalFooter>
                  <button type="button" className="btn btn-ghost" onClick={() => setRollenFuer(null)}>Abbrechen</button>
                  <button type="submit" className="btn btn-primary">Speichern</button>
                </ModalFooter>
              </form>
            </ModalContent>
          )}
        </Modal>
      )}
    </div>
  );
};

export default UserManagementPage;
